import React, {useCallback, useRef, useState, useEffect} from "react";
import AppModal from "../../core/components/app-modal";
import useModalWithData from "@common/hooks/use-modal-with-data";
import {ModalIds} from "@common/models/enums/modal-ids.enum";
import {animated} from "@react-spring/web";
import {useModalAnimation} from "../animations";
import AppButton from "@common/components/app-button";
import SpinnerLoader from "@common/components/spinner-loader";
import ImageCropper, {ImageCropperHandle} from "./image-cropper";
import {ReactComponent as CropIcon} from "@assets/icons/crop.svg";
import {ReactComponent as CrossIcon} from "@assets/icons/cross.svg";
import {ReactComponent as ZoomInIcon} from "@assets/icons/zoom-in.svg";
import {ReactComponent as ZoomOutIcon} from "@assets/icons/zoom-out.svg";
import {ReactComponent as InfoIcon} from "@assets/icons/info.svg";

export interface ImageCropperModalData {
    imageUrl: string;
    aspect?: number;
    cropShape?: 'rect' | 'round';
    onCropComplete?: (blob: Blob) => void;
}

const ImageCropperModal: React.FC = () => {
    const {data, hideModal, isOpenModal} = useModalWithData<ImageCropperModalData>(ModalIds.IMAGE_CROPPER);
    const {imageUrl, aspect = 1, cropShape = 'rect', onCropComplete} = data ?? {} as ImageCropperModalData;
    const {springProps, closeModal} = useModalAnimation(!!isOpenModal(), 'fast', 'swing');

    const cropperRef = useRef<ImageCropperHandle>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [zoom, setZoom] = useState(1);

    useEffect(() => {
        const id = setInterval(() => {
            if (cropperRef.current && cropperRef.current.zoom !== zoom) {
                setZoom(cropperRef.current.zoom);
            }
        }, 100);
        return () => clearInterval(id);
    }, [zoom]);

    const handleClose = useCallback(() => {
        closeModal(() => hideModal());
    }, [closeModal, hideModal]);

    const handleSave = useCallback(async () => {
        if (!cropperRef.current) return;
        try {
            setIsProcessing(true);
            const blob = await cropperRef.current.getCroppedBlob();
            onCropComplete?.(blob);
            handleClose();
        } catch (error) {
            console.error('Błąd podczas kadrowania:', error);
        } finally {
            setIsProcessing(false);
        }
    }, [onCropComplete, handleClose]);

    const handleZoomIn = () => cropperRef.current?.setZoom(0.1);
    const handleZoomOut = () => cropperRef.current?.setZoom(-0.1);
    const handleZoomSlider = (e: React.ChangeEvent<HTMLInputElement>) => {
        const target = Number(e.target.value) / 100;
        const delta = target - (cropperRef.current?.zoom ?? 1);
        cropperRef.current?.setZoom(delta);
    };

    if (!isOpenModal() || !imageUrl) return null;

    const aspectLabel = aspect === 1 ? '1:1' : `${aspect.toFixed(2)}:1`;
    const zoomPercent = Math.round(zoom * 100);

    return (
        <AppModal
            id={ModalIds.IMAGE_CROPPER}
            key={ModalIds.IMAGE_CROPPER}
            zIndex={50}
            forceFixed={true}
            classModal="min-h-full md:min-w-[600px] md:max-w-[calc(100%-100px)] max-h-svh flex justify-center overflow-hidden"
        >
            <animated.div
                style={springProps}
                className="flex flex-col bg-surface-level-1 rounded-2xl w-full max-w-2xl max-h-full overflow-hidden"
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-outline-mild">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-on-surface/5 flex items-center justify-center">
                            <CropIcon className="w-5 h-5 text-on-surface"/>
                        </div>
                        <div>
                            <p className="text-lg font-semibold text-on-surface leading-tight">Kadrowanie zdjęcia</p>
                            <p className="text-sm text-on-surface/60 leading-tight">Przesuń, przybliż i wybierz kadr</p>
                        </div>
                    </div>
                    <AppButton
                        onClick={handleClose}
                        aria-label="Zamknij"
                        className="w-9 h-9 !p-0 rounded-lg hover:bg-on-surface/5 flex items-center justify-center transition-colors"
                    >
                        <CrossIcon className="w-4 h-4 text-on-surface"/>
                    </AppButton>
                </div>

                <div className="p-5 bg-on-surface/[0.02] flex flex-col gap-3 overflow-y-auto">
                    <div className="relative">
                        <ImageCropper
                            ref={cropperRef}
                            imageUrl={imageUrl}
                            aspect={aspect}
                            cropShape={cropShape}
                        />
                        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1.5 bg-black/55 backdrop-blur-sm rounded-full text-white">
                            <CropIcon className="w-3.5 h-3.5 opacity-70"/>
                            <span className="text-xs font-medium tracking-wide">{aspectLabel}</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 px-3.5 py-2.5 bg-surface-level-1 rounded-[10px] border border-outline-mild">
                        <AppButton
                            onClick={handleZoomOut}
                            aria-label="Oddal"
                            className="w-9 h-9 !p-0 rounded-lg hover:bg-on-surface/5 flex items-center justify-center flex-shrink-0 transition-colors"
                        >
                            <ZoomOutIcon className="w-4 h-4 text-on-surface"/>
                        </AppButton>
                        <input
                            type="range"
                            min={50}
                            max={300}
                            step={1}
                            value={zoomPercent}
                            onChange={handleZoomSlider}
                            className="flex-1 accent-primary"
                        />
                        <AppButton
                            onClick={handleZoomIn}
                            aria-label="Przybliż"
                            className="w-9 h-9 !p-0 rounded-lg hover:bg-on-surface/5 flex items-center justify-center flex-shrink-0 transition-colors"
                        >
                            <ZoomInIcon className="w-4 h-4 text-on-surface"/>
                        </AppButton>
                        <span className="text-sm font-medium text-on-surface/60 min-w-[46px] text-right tabular-nums">
                            {zoomPercent}%
                        </span>
                    </div>

                    <p className="flex items-center gap-1.5 px-1 text-xs text-on-surface/50">
                        <InfoIcon className="w-3.5 h-3.5"/>
                        Przeciągnij ramkę, by zmienić kadr. Użyj kółka myszy lub suwaka do przybliżenia.
                    </p>
                </div>

                <div className="flex items-center justify-end gap-3 px-5 py-3.5 border-t border-outline-mild">
                    <AppButton
                        label="Anuluj"
                        disabled={isProcessing}
                        onClick={handleClose}
                        className="h-10 !px-5 !rounded-full !border-2 !border-on-surface text-sm font-semibold hover:bg-on-surface/5"
                    />
                    <AppButton
                        onClick={handleSave}
                        disabled={isProcessing}
                        className="h-10 !px-5 !rounded-full bg-primary hover:bg-primary/80 text-on-primary text-sm font-semibold flex items-center gap-1.5"
                    >
                        {isProcessing ? (
                            <SpinnerLoader color="#fff"/>
                        ) : (
                            <span>Zapisz kadr</span>
                        )}
                    </AppButton>
                </div>
            </animated.div>
        </AppModal>
    );
};

export default ImageCropperModal;
